/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's copyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * Shared building blocks for the localStorage/sessionStorage in-memory
 * consent wrappers: the RAM-backed `Storage` implementation, cookie/consent
 * parsing, and a generic contents snapshot helper.
 */

/**
 * In-memory storage implementation (pure RAM, no persistence).
 * Data is lost when the page reloads or the browser is closed.
 */
export class InMemoryStorage implements Storage {
  private data: Map<string, string> = new Map();

  get length(): number {
    return this.data.size;
  }

  clear(): void {
    this.data.clear();
  }

  getItem(key: string): string | null {
    return this.data.get(key) ?? null;
  }

  key(index: number): string | null {
    const keys = Array.from(this.data.keys());
    return keys[index] ?? null;
  }

  removeItem(key: string): void {
    this.data.delete(key);
  }

  setItem(key: string, value: string): void {
    this.data.set(key, value);
  }
}

/**
 * Parses a JSON consent payload and returns its status, defaulting to `pending`.
 *
 * @param raw The raw JSON string, or null when absent.
 * @returns The parsed status, or null when the payload is absent.
 */
export function parseConsentStatus(raw: string | null): string | null {
  if (raw === null) {
    return null;
  }
  return JSON.parse(raw).status || 'pending';
}

/**
 * Reads the consent status from the real `document.cookie`, if present.
 *
 * @returns The parsed status, or null when the cookie is absent.
 */
export function readDocumentConsentStatus(): string | null {
  const consentCookie = document.cookie
    .split(';')
    .find((cookie) => cookie.trim().startsWith('cdu_consent_v1='));
  if (!consentCookie) {
    return null;
  }
  return parseConsentStatus(decodeURIComponent(consentCookie.split('=')[1]));
}

/**
 * Gets the consent status from cookies, falling back to the caller-supplied
 * reader (each wrapper checks a different secondary source).
 *
 * @param readFallback Reads a raw consent payload when no cookie is present.
 * @returns {string} Consent status: 'pending', 'accepted', or 'rejected'
 */
export function getConsentStatus(
  readFallback: () => string | null,
): 'pending' | 'accepted' | 'rejected' {
  try {
    const status = readDocumentConsentStatus() ?? parseConsentStatus(readFallback()) ?? 'pending';
    return status as 'pending' | 'accepted' | 'rejected';
  } catch (error) {
    return 'pending';
  }
}

/**
 * Snapshots all key→value pairs from a Storage-like object into a plain map.
 *
 * @param store A Storage-like object exposing `length`, `key`, and `getItem`.
 * @returns An object mapping every present key to its value.
 */
export function snapshotStorage(
  store: Pick<Storage, 'length' | 'key' | 'getItem'>,
): Record<string, string> {
  const keys = Array.from({ length: store.length }, (_, i) => store.key(i));
  return keys.reduce<Record<string, string>>((contents, key) => {
    const value = key === null ? null : store.getItem(key);
    if (key !== null && value !== null) {
      contents[key] = value;
    }
    return contents;
  }, {});
}
